// src/screens/BasicDetails2.tsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { PrimaryButton } from "../components/Button";
import { updateProfile, getUserProfile } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import infoIcon from "../assets/info-icon.svg";

const toNumber = (value: unknown) => {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const hasBasicDetails2 = (userData: {
  hasGloves?: boolean | null;
  gloveSize?: number | string | null;
  hasHandwrap?: boolean | null;
}) => {
  const gloveSizeNumber = toNumber(userData.gloveSize);
  return (
    typeof userData.hasGloves === "boolean" &&
    typeof userData.hasHandwrap === "boolean" &&
    typeof gloveSizeNumber === "number" &&
    gloveSizeNumber > 0
  );
};

export default function BasicDetails2() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [hasGloves, setHasGloves] = useState<boolean | null>(null);
  const [gloveSize, setGloveSize] = useState<number | null>(8);
  const [hasHandwrap, setHasHandwrap] = useState<boolean | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!user?.id) {
        setInitialLoading(false);
        return;
      }
      try {
        const { user: u } = await getUserProfile(user.id);

        const step2Done = hasBasicDetails2(u);

        if (step2Done) {
          // Basic details 2 are already present, redirect to home
          console.log("Basic details 2 already present, redirecting to home");
          navigate("/student/home");
          return;
        }

        // Basic details 2 are incomplete, populate form with existing data
        setHasGloves(u.hasGloves ?? null);
        setGloveSize(u.gloveSize ?? 8);
        setHasHandwrap(u.hasHandwrap ?? null);
      } catch {
        // keep defaults
      } finally {
        setInitialLoading(false);
      }
    };
    load();
  }, [user?.id, navigate]);

  const handleGetStarted = useCallback(async () => {
    if (hasGloves === null || gloveSize === null || hasHandwrap === null) {
      setError("Please select all options.");
      return;
    }
    if (!user?.id) {
      setError("User not authenticated");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await updateProfile({ _id: user.id, hasGloves, gloveSize, hasHandwrap });
      navigate("/student/home");
    } catch {
      setError("Failed to update profile. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [hasGloves, gloveSize, hasHandwrap, navigate, user?.id]);

  const ynBtn = (selected: boolean) =>
    `flex-1 h-12 grid place-items-center border bg-white ${selected ? "border-[#D62422]" : "border-[#B1B1B1]"
    }`;

  const sizeBtn = (selected: boolean) =>
    `w-10 h-12 grid place-items-center border bg-white ${selected ? "border-[#D62422]" : "border-[#B1B1B1]"
    }`;

  if (initialLoading) {
    return (
      <div className="w-full bg-[#F7F7F7] min-h-[100dvh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D62422] mx-auto mb-4" />
          <p className="text-gray-600">Loading. Please wait.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-[#F7F7F7] min-h-[100dvh]">
      <div className="mx-auto max-w-[400px] min-h-[100dvh] flex flex-col">
        {/* top nav */}
        <Navbar onBack={() => navigate(-1)} />

        {/* content */}
        <div className="px-5 pt-2">
          {/* progress lines + title */}
          <div className="mb-7">
            <div className="flex items-center gap-3 mb-7">
              <div className="w-10 h-1 bg-[#D62422]" />
              <div className="w-10 h-1 bg-[#D62422]" />
            </div>
            <h1 className="font-manrope font-bold text-[20px] leading-[26px]">
              Setup your gears
            </h1>
          </div>

          {/* DO YOU HAVE GLOVES */}
          <section className="mb-6">
            <label className="block font-manrope font-extrabold text-[12px] tracking-[0.05em] uppercase mb-3">
              Do you have a gloves
            </label>

            <div className="flex gap-3">
              <button
                type="button"
                aria-pressed={hasGloves === true}
                onClick={() => setHasGloves(true)}
                className={ynBtn(hasGloves === true)}
              >
                <span className={hasGloves === true ? "text-black" : "text-[#717171] text-[12px]"}>Yes</span>
              </button>
              <button
                type="button"
                aria-pressed={hasGloves === false}
                onClick={() => setHasGloves(false)}
                className={ynBtn(hasGloves === false)}
              >
                <span className={hasGloves === false ? "text-black" : "text-[#717171] text-[12px]"}>No</span>
              </button>
            </div>

            {/* helper (only visible when No is selected) */}
            {hasGloves === false && (
              <div className="mt-3 flex items-center gap-2 text-[#717171]">
                <img src={infoIcon} alt="Info" className="w-5 h-5" />
                <span className="font-manrope text-[14px]">
                  Don't worry, your trainer will bring it
                </span>
              </div>
            )}
          </section>

          {/* YOUR GLOVE SIZE */}
          <section className="mb-7">
            <label className="block font-manrope font-extrabold text-[12px] tracking-[0.05em] uppercase mb-3">
              Your glove size (in oz)
            </label>
            <div className="flex gap-3">
              {[8, 10, 12, 14].map((s) => {
                const sizeMap: Record<number, string> = { 8: 'XS', 10: 'S', 12: 'M', 14: 'L' };
                return (
                  <button
                    key={s}
                    type="button"
                    aria-pressed={gloveSize === s}
                    onClick={() => setGloveSize(s)}
                    className={sizeBtn(gloveSize === s)}
                  >
                    <span className={`text-[12px] ${gloveSize === s ? "text-black" : "text-[#717171]"}`}>
                      {sizeMap[s]}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* DO YOU HAVE HANDWRAPS */}
          <section className="mb-2">
            <label className="block font-manrope font-extrabold text-[12px] tracking-[0.05em] uppercase mb-3">
              Do you have handwraps
            </label>

            <div className="flex gap-3">
              <button
                type="button"
                aria-pressed={hasHandwrap === true}
                onClick={() => setHasHandwrap(true)}
                className={ynBtn(hasHandwrap === true)}
              >
                <span className={hasHandwrap === true ? "text-black" : "text-[#717171] text-[12px]"}>Yes</span>
              </button>
              <button
                type="button"
                aria-pressed={hasHandwrap === false}
                onClick={() => setHasHandwrap(false)}
                className={ynBtn(hasHandwrap === false)}
              >
                <span className={hasHandwrap === false ? "text-black" : "text-[#717171] text-[12px]"}>No</span>
              </button>
            </div>

            {/* helper (only visible when No is selected) */}
            {hasHandwrap === false && (
              <div className="mt-3 flex items-center gap-2 text-[#717171]">
                <img src={infoIcon} alt="Info" className="w-5 h-5" />
                <span className="font-manrope text-[14px]">
                  Don't worry, your trainer will bring it
                </span>
              </div>
            )}
          </section>

          {error && <p className="mt-3 text-red-600 text-sm">{error}</p>}
        </div>

        {/* sticky footer CTA */}
        <div className="mt-auto w-full bg-white">
          <div className="mx-auto max-w-[400px] px-5 py-4 pb-[max(16px,env(safe-area-inset-bottom))]">
            <PrimaryButton
              onClick={handleGetStarted}
              disabled={loading}
              label={loading ? "UPDATING..." : "GET STARTED"}
              className="!w-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
